import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  TextField,
  Alert,
  Box,
  Typography
} from '@mui/material';
import axios from 'axios';

const API_BASE_URL = (import.meta.env.VITE_API_URL || (import.meta.env.PROD ? '' : 'http://localhost:5000')) + '/api';

function AccountConfirmationDialog() {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [numAccounts, setNumAccounts] = useState('1');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const checkStatus = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/members/account-confirmation/status`);
        if (cancelled) return;
        if (res.data.needsConfirmation) {
          setStatus(res.data);
          setNumAccounts(String(res.data.numAccounts ?? 1));
          setOpen(true);
        }
      } catch (err) {
        console.error('Failed to fetch account confirmation status:', err);
      }
    };
    checkStatus();
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async () => {
    const num = parseInt(numAccounts);
    if (isNaN(num) || num < 1) {
      setError('Please enter a valid number of accounts (at least 1).');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API_BASE_URL}/members/account-confirmation/confirm`, {
        numAccounts: num
      });
      setOpen(false);
    } catch (err) {
      console.error('Failed to confirm numAccounts:', err);
      setError(err.response?.data?.error || 'Failed to save. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!status) return null;

  const isFirstTime = status.isFirstTime;

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {isFirstTime ? 'Confirm your number of accounts' : 'Time to re-confirm your accounts'}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {isFirstTime
            ? `Welcome! Please tell us how many Omega accounts you currently play with on your main character ${status.mainCharacterName}.`
            : `It's been a while since you last confirmed how many accounts you play with on ${status.mainCharacterName}. Please re-confirm so our records stay accurate. We'll ask again every 3 months.`}
        </DialogContentText>

        <Alert severity="info" sx={{ mb: 2 }}>
          Count <strong>Omega accounts only</strong>. Alpha (free) accounts should not be included.
        </Alert>

        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Main character: <strong>{status.mainCharacterName}</strong>
          </Typography>
        </Box>

        <TextField
          autoFocus
          fullWidth
          label="Number of Omega accounts"
          type="number"
          value={numAccounts}
          onChange={(e) => setNumAccounts(e.target.value)}
          inputProps={{ min: 1, step: 1 }}
          disabled={submitting}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
        >
          {submitting ? 'Saving...' : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AccountConfirmationDialog;
